import { useLocation } from "react-router-dom";
import {
    type DriverCategoryOptionsType,
    driverPageLocation,
} from "../../utils/driverPageLocation";
import { useCompanyDepartmentSelector } from "../ReusableComponents/CompanyDepartmentSelector";
import {
    type LicenceStatusAnalysis,
    type CpcAndTachoAnalysis,
    type LicenceCategoriesAnalysis,
    type PhotocardAnalysis,
    type NewDriverAnalysis,
    type EyesightAnalysis,
    type NonAddMatchAnalysis,
    type FilterLicenceChangesDto,
} from "../../models/app/admin/driverFilters";
import {
    RiskProfileLevels,
    type GetAllDriversDto,
} from "../../models/app/admin/driver";
import { type FilterType } from "../../models/app/admin/filter";
import { useState } from "react";
import { driverListFiltersDefaultValues } from "../DriverListFilters/DriversListFiltersForm/driversListFiltersDefaultValues";
import { driverAnalysisDefaultValues } from "./driverAnalysisDefaultValues";
import {
    tableHeaderOptionsEnum,
    useTableHeaders,
} from "../../hooks/useTableHeaders";
import { driverTableAllowedIdsEn } from "../DriverList";
import { driverAddressTableAllowedIdsEn } from "../ReusableComponents/SharedTableContent/driverAddressTableContent";
import useTableStore from "../../stores/useTableStore";
import { match } from "ts-pattern";

export const useDriverAnalysisActions = () => {
    const location = useLocation();
    const sendToLocation = driverPageLocation(
        location.pathname
    ) as DriverCategoryOptionsType;
    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);
    const defaultVals = driverListFiltersDefaultValues(sendToLocation);
    const [isLoading, setIsLoading] = useState(false);

    const { updateTableConfig: updateDriverTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.allDrivers
    );
    const { updateTableConfig: updateProfDriverTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.professionalDrivers
    );

    const { selectedCompany, selectedDepartment } =
        useCompanyDepartmentSelector();
    const companyDepartmentVals: Pick<GetAllDriversDto, "company"> = {
        company: {
            ...(selectedCompany && { companyIds: [selectedCompany.companyId] }),
            ...(selectedDepartment && {
                departmentIds: [selectedDepartment.departmentId],
            }),
        },
    };

    const setScrollToTable = useTableStore((state) => state.setScrollToTable);

    const handleApplyFilter = async <AllowedIds extends string>(
        filter: FilterType,
        columns?: AllowedIds[]
    ) => {
        setIsLoading(true);
        setAppliedFilter(filter, sendToLocation);

        /** choose visible columns */
        if (columns) {
            sendToLocation === "drivers"
                ? updateDriverTableConfig(columns)
                : updateProfDriverTableConfig(columns);
        }

        setScrollToTable(true);
        setIsLoading(false);
    };

    const applyRiskCategoryFilter = (severityLevel: RiskProfileLevels[]) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                riskProfiles: severityLevel,
            },
        };

        handleApplyFilter(filter, [driverTableAllowedIdsEn.points]);
    };

    const applyCpcAndTachoFilter = (key: keyof CpcAndTachoAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    cpcAndTacho: {
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter(filter, [
            driverTableAllowedIdsEn.CpcExpiryEmailLastSent,
            driverTableAllowedIdsEn.TachoCardExpiryEmailLastSent,
            driverTableAllowedIdsEn.cpcLgvExpiryDate,
            driverTableAllowedIdsEn.cpcPcvExpiryDate,
            driverTableAllowedIdsEn.tachoCardExpiryDate,
        ]);
    };

    const applyLicenceStatusFilter = (key: keyof LicenceStatusAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    licenceStatus: {
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter(filter, [
            driverTableAllowedIdsEn.licenceCategory,
            driverTableAllowedIdsEn.licenceType,
            driverTableAllowedIdsEn.licenceStatus,
        ]);
    };

    const applyLicenceCategoriesFilter = (
        key: keyof LicenceCategoriesAnalysis
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    licenceCategories: {
                        [key]: true,
                    },
                },
            },
        };
        const columnsToShow = [
            match(key)
                .with(
                    "categoriesExpired",
                    () => driverTableAllowedIdsEn.expiredCategories
                )
                .with(
                    "categoriesDueToExpire",
                    () => driverTableAllowedIdsEn.expiringCategories
                )
                .with(
                    "entitlementExpiryEmailLastSent",
                    () => driverTableAllowedIdsEn.entitlementExpiryEmailLastSent
                )
                .exhaustive(),
            driverTableAllowedIdsEn.categoryExpiryDates,
            driverTableAllowedIdsEn.licenceType,
            driverTableAllowedIdsEn.entitlementExpiryEmailLastSent,
        ];

        handleApplyFilter(filter, columnsToShow);
    };

    const applyPhotocardFilter = (key: keyof PhotocardAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    photocard: {
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter(filter, [
            driverTableAllowedIdsEn.licenceExpires,
            driverTableAllowedIdsEn.licenceNumber,
            driverTableAllowedIdsEn.photoCardExpiryEmailLastSent,
        ]);
    };

    const applyNewDriverFilter = (key: keyof NewDriverAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    newDriver: {
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter(filter, [driverTableAllowedIdsEn.yearsHeld]);
    };

    const applyEyesightFilter = (key: keyof EyesightAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    eyesight: {
                        ...driverAnalysisDefaultValues.eyesight,
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter(filter, [driverTableAllowedIdsEn.driverRestrictions]);
    };

    const applyNonAddMatchFilter = (key: keyof NonAddMatchAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                driverAnalysis: {
                    nonAddMatch: {
                        ...driverAnalysisDefaultValues.nonAddMatch,
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter(filter, [
            driverAddressTableAllowedIdsEn.addressMatch,
            driverAddressTableAllowedIdsEn.postcode,
            driverTableAllowedIdsEn.AddressNonMatchEmailLastSent,
        ]);
    };

    const applyLicenceChangesFilter = (key: keyof FilterLicenceChangesDto) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                licenceChanges: {
                    [key]: true,
                },
            },
        };

        handleApplyFilter(filter, [driverTableAllowedIdsEn.endorsementCodes]);
    };

    return {
        applyRiskCategoryFilter,
        applyCpcAndTachoFilter,
        applyLicenceStatusFilter,
        applyLicenceCategoriesFilter,
        applyPhotocardFilter,
        applyNewDriverFilter,
        applyEyesightFilter,
        applyNonAddMatchFilter,
        applyLicenceChangesFilter,
        isLoading,
    };
};
